import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2, Beef, Users, Truck, Snowflake, Flame } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1775222678831-6ff176ab4a58?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const STEAK = "https://images.unsplash.com/photo-1728042359930-c0145f0fd442?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const PERKS = [
  { icon: Beef, title: "Quality meats", body: "Hand-raised cuts you'll be proud to serve your own family." },
  { icon: Users, title: "Real ranchers", body: "A closer connection to the hardworking producers behind your food." },
  { icon: Truck, title: "To your doorstep", body: "Our membership marketplace brings the mission straight to you." },
  { icon: Snowflake, title: "A full freezer", body: "Food you're excited to cook — and a purchase that supports local." },
];

export default function FoundingOffer() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [offer, setOffer] = useState(null);
  const [seats, setSeats] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/founding/offer").then(({ data }) => setOffer(data)).catch(() => {});
    const load = () => api.get("/launch/founding-count").then(({ data }) => setSeats(data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  const claim = async () => {
    if (!user) { nav("/register?next=/founding"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/founding/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  const billingLabel = offer?.billing_date_label || "Black Friday, November 27, 2026";

  return (
    <div className="bg-[var(--ff-bg)]" data-testid="founding-offer-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Cattle grazing at sunrise" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-black/40" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 pt-24 pb-20 sm:pt-32 sm:pb-28 text-white">
          <span className="inline-block overline text-[var(--ff-secondary)] mb-4" data-testid="founding-badge">Founding Member · Invitation only</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Good food starts with good people.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-white/85 max-w-2xl">
            The people who care about the land. Who get up before sunrise. Who believe the food they
            raise should be something they're proud to serve their own families. Those are the people
            we're building <strong>The Farm To You</strong> around.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              onClick={claim}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--ff-secondary)] text-black font-semibold px-8 py-4 text-base hover:brightness-105 transition-all disabled:opacity-60"
              data-testid="founding-claim-btn"
            >
              {busy ? <Loader2 size={18} className="animate-spin" /> : null}
              Become a member — pay just $1 today →
            </button>
            {!user && (
              <span className="text-sm text-white/70">
                Already have an account? <Link to="/login?next=/founding" className="underline" data-testid="founding-login-link">Sign in</Link>
              </span>
            )}
          </div>

          {seats && !seats.is_full && (
            <div className="mt-6 max-w-md" data-testid="founding-seats-counter">
              <div className="flex items-center justify-between text-xs text-white/80 mb-1.5">
                <span className="inline-flex items-center gap-1.5 font-semibold">
                  <Flame size={13} className="text-[var(--ff-secondary)]" />
                  <span className="tabular-nums" data-testid="founding-seats-remaining">{seats.remaining.toLocaleString()}</span> founding spots left
                </span>
                <span className="text-white/55">{seats.count.toLocaleString()} of {seats.cap.toLocaleString()} claimed</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-white/15 overflow-hidden">
                <div className="h-full rounded-full bg-[var(--ff-secondary)] transition-all duration-700" style={{ width: `${Math.max(3, seats.pct_filled)}%` }} />
              </div>
            </div>
          )}
          <p className="mt-4 text-xs text-white/60 max-w-xl">
            $1 due today, credited toward your membership. The remaining {offer ? `$${offer.due_on_launch}` : "$149"} of
            your $150 annual membership is charged on {billingLabel}. Auto-renews at $150/year. Products purchased separately.
          </p>
        </div>
      </section>

      {/* Belief */}
      <section className="max-w-3xl mx-auto px-6 py-16 sm:py-20">
        <p className="overline text-[var(--ff-primary)]">Our belief</p>
        <p className="mt-4 text-lg sm:text-xl leading-relaxed text-[var(--ff-text-primary)]">
          We started with a simple belief: you deserve quality food, a closer connection to where it
          comes from, and an easier way to support local ranchers across America. Our membership
          marketplace brings that mission to your doorstep — connecting you with quality meats and the
          hardworking producers behind them.
        </p>
        <p className="mt-6 text-base text-[var(--ff-text-secondary)]">
          We officially launch Black Friday, November 27, 2026. And you can join today for just <strong>$1</strong>.
        </p>
      </section>

      {/* Perks */}
      <section className="max-w-5xl mx-auto px-6 pb-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PERKS.map((p) => (
            <div key={p.title} className="card-flat p-6" data-testid={`founding-perk-${p.title.split(" ")[0].toLowerCase()}`}>
              <p.icon size={24} className="text-[var(--ff-primary)]" />
              <h3 className="mt-3 font-semibold text-[var(--ff-text-primary)]">{p.title}</h3>
              <p className="mt-1 text-sm text-[var(--ff-text-secondary)]">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Offer card */}
      <section className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
        <div className="grid md:grid-cols-2 gap-8 items-center card-flat overflow-hidden">
          <img src={STEAK} alt="Dry-aged ribeye" className="w-full h-64 md:h-full object-cover" />
          <div className="p-8">
            <p className="overline text-[var(--ff-secondary)]">The founding offer</p>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-display text-5xl text-[var(--ff-text-primary)]">$1</span>
              <span className="text-[var(--ff-text-muted)]">today</span>
            </div>
            <ul className="mt-5 space-y-3 text-sm text-[var(--ff-text-secondary)]">
              {[
                "Lock in your founding membership before launch",
                `Just $${offer?.due_on_launch ?? 149} more on ${billingLabel} (your $1 is credited)`,
                "Member pricing, free shipping perks & members-only drops",
                "Auto-renews at $150/year — cancel anytime",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <Check size={16} className="text-[var(--ff-primary)] mt-0.5 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={claim}
              disabled={busy}
              className="btn-primary w-full mt-7 justify-center"
              data-testid="founding-claim-btn-2"
            >
              {busy ? <Loader2 size={18} className="animate-spin" /> : null}
              Pay $1 & become a Founding Member
            </button>
            <p className="mt-3 text-[11px] text-[var(--ff-text-muted)]">
              From their farms to your family — The Farm To You.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
